import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { WBAlert, WBBox, WBFlex, WBTypography } from '@admiin-com/ds-web';
import AdmiinLogo from '../../components/AdmiinLogo/AdmiinLogo';
import { useSearchParams } from 'react-router-dom';
import { PageContainer } from '../../components';
import { isLoggedInVar } from '@admiin-com/ds-graphql';
import NewPasswordForm, { NewPasswordFormData } from './NewPasswordForm';
import SignInForm, {
  ConfirmMfaFormData,
  ConfirmSignUpFormData,
  SignInFormData,
} from './SignInForm';
import { useSignIn } from '../../hooks/useSignIn/useSignIn';
import { PATHS } from '../../navigation/paths';
import { useRedirectIfAuthenticated } from '../../hooks/useRedirectIfAuthenticated/useRedirectIfAuthenticated';

const SignIn = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { isCheckingAuth } = useRedirectIfAuthenticated(PATHS.entities);

  const {
    authStatus,
    authError,
    isNewPasswordRequired,
    mfaCodeRequired,
    mfaType,
    codeDelivery,
    deliveryDestination,
    submittedEmail,
    submittedPassword,
    userError,
    onSignIn,
    onSubmitNewPassword,
    onConfirmSignUp,
    onConfirmMfa,
    onResendCode,
    handleInvitationSignIn,
    setSubmittedEmail,
    setSubmittedPassword,
  } = useSignIn();

  useEffect(() => {
    if (!isCheckingAuth) {
      isLoggedInVar(false);
      localStorage.removeItem('sub');
    }
  }, [isCheckingAuth]);

  useEffect(() => {
    // Check if we have invitation parameters
    const usernameParam = searchParams.get('u') || searchParams.get('email');
    const codeParam = searchParams.get('c') || searchParams.get('password');

    const username = usernameParam ? decodeURIComponent(usernameParam) : '';
    const code = codeParam ? decodeURIComponent(codeParam) : '';

    if (username) {
      setSubmittedEmail(username);
    }
    if (code) {
      setSubmittedPassword(code);
    }

    // If we have both username and code from the URL, attempt auto sign-in
    if (username && code) {
      handleInvitationSignIn(username, code);
    }
  }, [
    searchParams,
    handleInvitationSignIn,
    setSubmittedEmail,
    setSubmittedPassword,
  ]);

  const handleSignIn = (data: SignInFormData) => {
    if (data.email && data.password) {
      onSignIn({ email: data.email, password: data.password });
    }
  };
  const handleNewPassword = (data: NewPasswordFormData) =>
    onSubmitNewPassword(data);
  const handleConfirmSignUp = (data: ConfirmSignUpFormData) =>
    onConfirmSignUp(data);
  const handleConfirmMfa = (data: ConfirmMfaFormData) => onConfirmMfa(data);

  if (isCheckingAuth) {
    return null;
  }

  return (
    <PageContainer
      sx={{
        padding: 0,
        height: '100%',
        overflowY: 'auto',
      }}
    >
      <WBFlex
        flex={1}
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        paddingTop={[0, 6]}
        paddingBottom={[0, 3]}
        mx={2}
        position="relative"
      >
        <WBBox sx={{ mb: { md: 5, xs: 1 }, textAlign: 'center' }}>
          <WBBox sx={{ display: 'flex', justifyContent: 'center' }}>
            <AdmiinLogo />
          </WBBox>
          {!mfaCodeRequired && !codeDelivery && !isNewPasswordRequired && (
            <WBTypography variant="h2" sx={{ mt: { xs: 5, sm: 2 } }}>
              {t('signInTitle', { ns: 'common' })}
            </WBTypography>
          )}
          {codeDelivery && isNewPasswordRequired && (
            <WBTypography variant="h2" sx={{ mt: { xs: 5, sm: 2 } }}>
              {t('verificationSent', { ns: 'common' })}
            </WBTypography>
          )}
          {mfaCodeRequired && (
            <WBTypography variant="h2" sx={{ mt: { xs: 5, sm: 2 } }}>
              {t('mfaCodeRequired', { ns: 'authentication' })}
            </WBTypography>
          )}
        </WBBox>

        {(mfaCodeRequired || (codeDelivery && !isNewPasswordRequired)) && (
          <WBBox mb={7}>
            {mfaCodeRequired && (
              <WBTypography
                textAlign="center"
                variant="body2"
                fontWeight="regular"
              >
                {t(mfaType === 'SMS' ? 'enterSmsMfaCode' : 'enterMfaCode', {
                  ns: 'authentication',
                })}
              </WBTypography>
            )}
            {codeDelivery && !isNewPasswordRequired && (
              <WBTypography
                textAlign="center"
                variant="body2"
                fontWeight="regular"
              >
                {t(
                  codeDelivery === 'SMS'
                    ? 'enterVerificationCodeSentSMS'
                    : 'enterVerificationCodeSentEmail',
                  { ns: 'common' }
                )}{' '}
                <WBBox as="span" fontWeight="medium">
                  {deliveryDestination?.replace('+', '')}
                </WBBox>
              </WBTypography>
            )}
          </WBBox>
        )}

        {!isNewPasswordRequired && (
          <SignInForm
            isNewPasswordRequired={isNewPasswordRequired}
            mfaCodeRequired={mfaCodeRequired}
            authError={authError}
            authStatus={authStatus}
            codeDelivery={codeDelivery}
            onSignIn={handleSignIn}
            onConfirmSignUp={handleConfirmSignUp}
            onConfirmMfa={handleConfirmMfa}
            onResendCode={onResendCode}
            defaultEmail={submittedEmail}
            defaultPassword={submittedPassword}
          />
        )}

        {isNewPasswordRequired && (
          <NewPasswordForm
            submittedEmail={submittedEmail}
            authError={authError}
            authStatus={authStatus}
            oldPassword={submittedPassword}
            onSubmit={handleNewPassword}
          />
        )}

        {userError?.message && (
          <WBAlert title={userError.message} severity="error" />
        )}
        <WBFlex sx={{ flexGrow: 1 }} />
      </WBFlex>
    </PageContainer>
  );
};

export default SignIn;
